import { put, delay, takeLatest } from "@redux-saga/core/effects";
import { createAnnouncement, deleteAnnouncement, fetchAllAnnouncements, fetchAnnouncementById, readAnnouncement, sendAnnouncementToUsers } from "../../../Utils/BackendAPICalls";
import { Actions, Types } from "../../Reducer/rootReducer";
import { Announcements, SendAnnouncementOptions } from "../../../Types/UnitFeatureTypes";

interface CreateAnnouncementAction {
    payload: {
        announcementData: Announcements;
    };
}

interface DeleteAnnouncementAction {
    payload: {
        id: string;
    };
}

interface FetchAnnouncementAction {
    payload: {
        id?: string;
        offset?: number;
        sortColumn?: string;
        sortDirection?: string;
    };
}

interface SendAnnouncementAction {
    payload: {
        announcement_id: string;
        user_ids: string[];
        sendSelection: SendAnnouncementOptions;
    };
}

interface ReadAnnouncementAction {
    payload: {
        announcement_id: string;
        user_id: string;
    };
}

export function* createAnnouncementRequestFunction(action: CreateAnnouncementAction): Generator<any, void, Response> {
    const { announcementData } = action.payload;
    try {
        yield delay(100);
        const response: any = yield createAnnouncement(announcementData);
        if (!response || response?.error) {
            yield put(Actions.announcement.createAnnouncementRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.announcement.createAnnouncementRequestAction.success({ id: response.id }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.announcement.createAnnouncementRequestAction.failure(error));
    }
}

export function* deleteAnnouncementRequestFunction(action: DeleteAnnouncementAction): Generator<any, void, Response> {
    const { id } = action.payload;
    try {
        yield delay(100);
        const response: any = yield deleteAnnouncement(id);
        if (!response || response?.error) {
            yield put(Actions.announcement.deleteAnnouncementRequestAction.failure({ message: response.error }));
        }
        yield put(Actions.announcement.deleteAnnouncementRequestAction.success());
        yield put(Actions.fetchAnnouncement.announcementActions.request({
            offset: 0,
            sortColumn: 'created_at',
            sortDirection: 'desc'
        }));
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.announcement.deleteAnnouncementRequestAction.failure(error));
    }
}

export function* sendAnnouncementToUsersFunction(action: SendAnnouncementAction): Generator<any, void, Response> {
    const { announcement_id, user_ids, sendSelection } = action.payload;
    try {
        yield delay(100);
        const response: any = yield sendAnnouncementToUsers({ announcement_id, user_ids, sendSelection });
        if (!response || response?.error) {
            yield put(Actions.announcement.sendAnnouncementRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.announcement.sendAnnouncementRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.announcement.sendAnnouncementRequestAction.failure(error));
    }
}

export function* fetchAnnouncementsRequestFunction(action: FetchAnnouncementAction): Generator<any, void, Response> {
    const { id, offset, sortColumn, sortDirection } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        if (id) {
            response = yield fetchAnnouncementById(id);
        } else {
            response = yield fetchAllAnnouncements(sortColumn || 'created_on', sortDirection || 'desc', offset ?? 0);
        }
        if (!response || response?.error) {
            yield put(Actions.fetchAnnouncement.announcementActions.failure({ message: response.error }));
        }
        yield put(Actions.fetchAnnouncement.announcementActions.success({ announcements: response.announcements, announcement: response.announcement, count: response.count }));
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.fetchAnnouncement.announcementActions.failure(error));
    }
}

export function* readAnnouncementRequestFunction(action: ReadAnnouncementAction): Generator<any, void, Response> {
    const { announcement_id, user_id } = action.payload;
    try {
        yield delay(100);
        const response: any = yield readAnnouncement({ announcement_id, user_id });
        if (!response || response?.error) {
            yield put(Actions.announcement.readAnnouncementRequestAction.failure({ message: response.error }));
        }
        yield put(Actions.announcement.readAnnouncementRequestAction.success());
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.announcement.readAnnouncementRequestAction.failure(error));
    }
}

export default function* announcementSaga() {
    yield takeLatest(Types.CREATE_ANNOUNCEMENT_REQUEST, createAnnouncementRequestFunction);
    yield takeLatest(Types.DELETE_ANNOUNCEMENT_REQUEST, deleteAnnouncementRequestFunction);
    yield takeLatest(Types.FETCH_ANNOUNCEMENT_REQUEST, fetchAnnouncementsRequestFunction);
    yield takeLatest(Types.SEND_ANNOUNCEMENT_REQUEST, sendAnnouncementToUsersFunction);
    yield takeLatest(Types.READ_ANNOUNCEMENT_REQUEST, readAnnouncementRequestFunction);
}
